import { Link } from "@tanstack/react-router";

import { HStack, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import type { WorkerType } from "@/constants/workerType";
import { LabelPerson } from "@/features/person/components/LabelPerson";
import { PersonPreviewContent } from "@/features/person/components/PreviewCard/PersonPreviewContent";
import { defaultNS, useTranslation } from "@/lib/i18n";

import { UnknownPreviewContent } from "./unknown-preview-content";

type User = {
  id: string;
  email?: string;
  workerNumber?: string;
  worker?: {
    personId: string;
    personName: string;
    workerEmail?: string;
    avatarUrl?: string;
    workerType?: WorkerType;
  };
};
type Worker = NonNullable<User["worker"]>;

function Person({
  worker,
  showDetail,
}: {
  worker: Worker;
  showDetail: boolean;
}) {
  const previewCard = (
    <PersonPreviewContent person={worker} showProfileLink={true} />
  );

  return (
    <HStack className="gap-3">
      <LabelPerson
        name={worker.personName}
        avatarUrl={worker.avatarUrl}
        showLabel={false}
        size="Large"
        previewCardContent={previewCard}
      />
      {showDetail && (
        <VStack className="gap-1">
          <Link
            to={"/person/$id"}
            params={{ id: worker.personId }}
            className="hover:underline"
          >
            <Text variant="label">{worker.personName}</Text>
          </Link>
          {worker.workerEmail && (
            <Text variant="labelSmall" className="text-neutral-muted-on-fill">
              {worker.workerEmail}
            </Text>
          )}
        </VStack>
      )}
    </HStack>
  );
}

// TODO: MemberAvatarGroup に移行
function Unknown({
  user,
  showDetail,
}: {
  user: Omit<User, "worker">;
  showDetail: boolean;
}) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "component.dataTable.cells.PersonCell",
  });

  const previewCard = (
    <UnknownPreviewContent
      person={{
        email: user.email,
        workerNumber: user.workerNumber,
      }}
    />
  );

  return (
    <HStack className="gap-3">
      <LabelPerson size="Large" previewCardContent={previewCard} />
      {showDetail && (
        <VStack className="gap-1">
          <Text variant="label" className="text-error-on-neutral-fill">
            {t("unknownPerson")}
          </Text>
          {user.email && (
            <Text variant="labelSmall" className="text-neutral-muted-on-fill">
              {user.email}
            </Text>
          )}
        </VStack>
      )}
    </HStack>
  );
}

type Props = {
  user: User;
  /** 名前、メアドを表示します */
  showDetail?: boolean;
};

export function ShadowItUser({ user, showDetail = false }: Props) {
  if (user.worker) {
    return <Person worker={user.worker} showDetail={showDetail} />;
  }

  return <Unknown user={user} showDetail={showDetail} />;
}
